import { createContext, ReactNode, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ChatEditingContextType = {
  editingChatId: number | null;
  setEditingChatId: (id: number | null) => void;
  isEditingLeaveModalOpen: boolean;
  openEditingLeaveModal: (to: string) => void;
  confirmEditingLeaveModal: () => void;
  closeEditingLeaveModal: () => void;
  pendingNavigation: string | null;
};

const ChatEditingContext = createContext<ChatEditingContextType | undefined>(undefined);

export const ChatEditingProvider = ({ children }: { children: ReactNode }) => {
  const [editingChatId, setEditingChatId] = useState<number | null>(null);
  const [isEditingLeaveModalOpen, setIsEditingLeaveModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const navigate = useNavigate();

  const openEditingLeaveModal = (to: string) => {
    setPendingNavigation(to);
    setIsEditingLeaveModalOpen(true);
  };

  const confirmEditingLeaveModal = () => {
    setEditingChatId(null);
    setIsEditingLeaveModalOpen(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const closeEditingLeaveModal = () => {
    setPendingNavigation(null);
    setIsEditingLeaveModalOpen(false);
  };

  return (
    <ChatEditingContext.Provider
      value={{
        editingChatId,
        setEditingChatId,
        isEditingLeaveModalOpen,
        openEditingLeaveModal,
        confirmEditingLeaveModal,
        closeEditingLeaveModal,
        pendingNavigation,
      }}
    >
      {children}
    </ChatEditingContext.Provider>
  );
};

export const useChatEditing = () => {
  const context = useContext(ChatEditingContext);
  if (!context) {
    throw new Error('useChatEditing must be used within a ChatEditingProvider');
  }
  return context;
};
